import { useRef, useState, type FormEvent } from "react";
import posthog from "posthog-js";
import { subscribeNewsletter } from "@/lib/api";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Something went wrong. Please try again.";
}

export type NewsletterVariant = "hero" | "sidebar" | "inline" | "footer";

export interface NewsletterFormState {
  email: string;
  setEmail: (v: string) => void;
  submitted: boolean;
  error: string | null;
  loading: boolean;
  honeypot: string;
  setHoneypot: (v: string) => void;
  handleSubmit: (e: FormEvent) => void;
  clearError: () => void;
}

export function useNewsletterForm(
  variant: NewsletterVariant,
): NewsletterFormState {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const mountTime = useRef(Date.now());

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Honeypot: bots fill this hidden field, humans don't.
    if (honeypot) {
      setSubmitted(true);
      return;
    }

    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }
    if (!EMAIL_REGEX.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      await subscribeNewsletter({ data: { email, t: mountTime.current } });
      posthog.identify(email);
      posthog.capture("newsletter_subscribed", { variant });
      setSubmitted(true);
      setEmail("");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    setEmail,
    submitted,
    error,
    loading,
    honeypot,
    setHoneypot,
    handleSubmit,
    clearError: () => setError(null),
  };
}

export interface NewsletterFormClasses {
  /** Classes on <form> (layout: flex direction, gap). */
  form: string;
  /** Base input classes applied regardless of error state. */
  input: string;
  /** Applied to input when error is active (border/ring tint). */
  inputError: string;
  /** Applied to input when no error. */
  inputDefault: string;
  /** Submit button classes. */
  button: string;
  /** Error <p> classes. */
  error: string;
  /** If true, error is rendered in a <div> wrapper next to the input (compact variants). */
  errorInline?: boolean;
}

interface NewsletterFormFieldsProps {
  form: NewsletterFormState;
  classes: NewsletterFormClasses;
  placeholder: string;
  buttonText: string;
}

export default function NewsletterFormFields({
  form,
  classes,
  placeholder,
  buttonText,
}: NewsletterFormFieldsProps) {
  const {
    email,
    setEmail,
    error,
    loading,
    honeypot,
    setHoneypot,
    handleSubmit,
    clearError,
  } = form;
  const inputClass = `${classes.input} ${error ? classes.inputError : classes.inputDefault}`;
  const errorEl = error ? <p className={classes.error}>{error}</p> : null;

  const inputField = (
    <>
      <input
        type="email"
        placeholder={placeholder}
        aria-label="Email address"
        className={inputClass}
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          clearError();
        }}
      />
      {classes.errorInline && errorEl}
    </>
  );

  return (
    <>
      <form onSubmit={handleSubmit} noValidate className={classes.form}>
        {classes.errorInline ? <div>{inputField}</div> : inputField}
        <input
          type="text"
          name="website_url"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          style={{ display: "none" }}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />
        <button type="submit" disabled={loading} className={classes.button}>
          {loading ? "Subscribing..." : buttonText}
        </button>
      </form>
      {!classes.errorInline && errorEl}
    </>
  );
}
